import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import Ordersdetails from '../comp/OrdersDetails.jsx'


export const OrderReport = ({ reportRange, rawData }) => {
    const theme = useTheme();

    const [chartData, setChartData] = useState([]);
    const [topItems, setTopItems] = useState([]);
    const [orderSummary, setOrderSummary] = useState({
        total: 0,
        dineIn: 0,
        takeOut: 0,
        online: 0,
        voided: 0,
        completed: 0,
    });
    
    // Helper function to map order type code to name
    const getOrderTypeName = (typeCode) => {
        switch (typeCode) {
            case 0: return 'Dine In (In-store)';
            case 1: return 'Take Out (Pickup)';
            case 2: return 'Kiosk (Online)';
            case 3: return 'Online';
            case 4: return 'Drive-Thru';
            case 5: return '3rd Party Delivery';
            default: return 'Other';
        }
    };


    useEffect(() => {
        // --- DATA SOURCING ---
        let ordersArray = rawData?.orders || []; // Assuming rawData is { dailyData: [...], orders: [...] }

        if (ordersArray.length === 0) {
            const stored = sessionStorage.getItem("orderReportData");
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    ordersArray = parsed.rawData?.orders || parsed.rawData || []; 
                } catch (err) {
                    console.error("Failed to parse stored order report data:", err);
                    ordersArray = [];
                }
            }
        }
        console.log(ordersArray);
        if (ordersArray.length === 0) {
             setChartData([]);
             setTopItems([]);
             setOrderSummary({ total: 0, dineIn: 0, takeOut: 0, online: 0, voided: 0, completed: 0 });
             return;
        }

        // --- 1. Chart Data & Summary Counters ---
        const hourlyMap = {};
        const summary = {
            total: 0,
            dineIn: 0,
            takeOut: 0,
            online: 0,
            voided: 0,
            completed: 0,
        };
        const itemMap = {};

        ordersArray.forEach(order => {
            
            // Check for voided orders (oStatus === 5)
            if (order.oStatus === 5) {
                summary.voided += 1;
                summary.total += 1;
                return; // Skip remaining processing for voided orders
            }
            
            // Process COMPLETED Orders (oStatus === 2)
            if (order.oStatus === 2) {
                summary.completed += 1;
                summary.total += 1;
                
                // Hourly Sales Trend (Counting Orders per Hour)
                const hour = new Date(order.startTime).getHours();
                hourlyMap[hour] = (hourlyMap[hour] || 0) + 1;
                
                // Detailed Order Type Count
                if (order.type === 0) summary.dineIn += 1;
                else if (order.type === 1) summary.takeOut += 1;
                else if (order.type === 3) summary.online += 1;
                else if (order.type === 2) summary.online += 1; // Assuming Kiosk goes under Online/In-store, using Online for simplicity
                else summary.other += 1;


                // Top Items Calculation
                order.orders.forEach(subOrder => {
                    subOrder.order?.forEach(item => {
                        itemMap[item.item] = (itemMap[item.item] || 0) + item.quantity;
                    });
                });
            }
        });

        // Finalize chart data
        const chart = Object.entries(hourlyMap).map(([hour, count]) => ({
            hour: `${hour.toString().padStart(2, "0")}:00`,
            orders: count
        }));

        // Finalize top items
        const topItemsList = Object.entries(itemMap)
            .map(([item, qty]) => ({ item, qty }))
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 10);

        setChartData(chart);
        setOrderSummary(summary);
        setTopItems(topItemsList);

    }, [reportRange, rawData]);

    // Format top selling items table data
    const formattedTopItems = topItems.map(row => (
        <TableRow key={row.item}>
            <TableCell>{row.item}</TableCell>
            <TableCell align="right">{row.qty}</TableCell>
        </TableRow>
    ));
    
    // Format order summary table data
    const summaryRows = [
        { label: "Total Orders", value: orderSummary.total },
        { label: "Completed", value: orderSummary.completed },
        { label: "Voided", value: orderSummary.voided },
        { label: "Dine In", value: orderSummary.dineIn },
        { label: "Take Out", value: orderSummary.takeOut },
        { label: "Online", value: orderSummary.online },
    ];


    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            {/* 1. Order Timeline */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Orders by Hour
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="orders" stroke={theme.palette.primary.main} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>


            <Grid container spacing={3}>
                {/* 2. Order Summary */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#fff3e0" }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableBody>
                                    {summaryRows.map(row => (
                                        <TableRow key={row.label}>
                                            <TableCell>{row.label}</TableCell>
                                            <TableCell align="right">{row.value}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* 3. Top Items */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#e3f2fd" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Top Selling Items
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell align="right">Quantity</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {formattedTopItems}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: 3, my: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Orders Details
                </Typography>

                {/* Ordersdetails needs access to the raw data, which is passed down */}
                <Ordersdetails orders={rawData?.orders || []} showPagination={true}></Ordersdetails>

            </Paper>
        </Container>
    );
};